import {
  ExternalPlaidAccount,
  InternalAdvanceAccount,
  InternalChargeCardAccount,
  InternalCheckingAccount,
  InternalSavingsAccount,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { isInternalAdvanceAccount } from 'features/accounts/helpers/Account.helpers';
import { useCallback } from 'react';
import GetAccountR2 from 'services/graphql/queries/AccountR2';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { useQuery } from 'urql';

export interface IAccountTypes {
  accountR2:
    | ExternalPlaidAccount
    | InternalCheckingAccount
    | InternalSavingsAccount
    | InternalAdvanceAccount
    | InternalChargeCardAccount;
}

const useAccountDetails = (accountId: string) => {
  const router = useRouter();
  const [{ data, error, fetching }, executeQuery] = useQuery<IAccountTypes>({
    query: GetAccountR2,
    variables: { id: accountId },
    requestPolicy: 'network-only',
    pause: !(accountId && accountId.trim()),
  });

  const { defaultTitle, defaultMessage, cancelButtonTitle } =
    useDefaultErrorStrings();

  const cancel = () => {
    router.back();
  };
  const buttons = {
    primaryButton: {
      title: cancelButtonTitle,
      onPress: cancel,
    },
  };

  const errorModel = error?.message
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: defaultTitle,
        messageBody: error.message ?? defaultMessage,
        ...buttons,
      }
    : null;

  const fetchAccountDetails = useCallback(() => {
    if (accountId) {
      executeQuery({ id: accountId });
    }
  }, [executeQuery, accountId]);

  const accountDetails = data?.accountR2;
  const advanceAccountDetails = isInternalAdvanceAccount(accountDetails)
    ? accountDetails
    : undefined;
  const activeAdvance = advanceAccountDetails?.activeAdvance;
  const hasActiveAdvance =
    activeAdvance !== null && activeAdvance !== undefined;

  return {
    accountDetails,
    pastAdvances: advanceAccountDetails?.pastAdvances,
    hasActiveAdvance: hasActiveAdvance,
    errorModel: errorModel,
    loading: fetching,
    refetch: fetchAccountDetails,
  };
};

export default useAccountDetails;
